import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import moment from "moment-timezone";

// Initial state
const initialState = {
  appointments: [],
  settings: null,
  sectionTemplates: [],
  selectedEvent: null,
  view: "month",
  selectedDate: new Date().toISOString(),
  dateRange: {
    start: moment().startOf("month").format("YYYY-MM-DD"),
    end: moment().endOf("month").format("YYYY-MM-DD"),
  },
  filters: {
    searchQuery: "",
  },
  loading: {
    appointments: false,
    settings: false,
    sectionTemplates: false,
    create: false,
    update: false,
    delete: false,
    jobSetup: false,
    appointmentDetail: false,
  },
  error: {
    appointments: null,
    settings: null,
    sectionTemplates: null,
    create: null,
    update: null,
    delete: null,
    jobSetup: null,
    appointmentDetail: null,
  },
  metadata: {
    total_count: 0,
  },
};

// Async thunks
export const fetchAppointmentsByDateRange = createAsyncThunk(
  "calendar/fetchAppointmentsByDateRange",
  async ({ start, end }, { getState, rejectWithValue }) => {
    try {
      const token = getState().login?.token;
      if (!token) {
        // Return empty data if no token (for testing without auth)
        return {
          appointments: [],
          metadata: { total_count: 0 },
          date_range: { start, end },
        };
      }

      const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

      const response = await fetch(
        `${baseUrl}/calendar/appointments/by_date_range/?start=${start}&end=${end}`,
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCalendarSettings = createAsyncThunk(
  "calendar/fetchSettings",
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getState().login?.token;
      if (!token) {
        // Return default settings if no token
        return {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          first_day_of_week: 0,
          default_calendar_view: "month",
        };
      }

      const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

      const response = await fetch(`${baseUrl}/calendar/settings/me`, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSectionTemplates = createAsyncThunk(
  "calendar/fetchSectionTemplates",
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getState().login?.token;
      if (!token) {
        throw new Error("Authentication required");
      }

      const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

      const response = await fetch(
        `${baseUrl}/section-templates/?include_current_version=false`,
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Extract only id and name from results
      const sectionTemplates =
        data.results?.map((template) => ({
          id: template.id,
          name: template.name,
          isSystemDefault: template.is_system_default,
        })) || [];
      return sectionTemplates;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAppointmentDetail = createAsyncThunk(
  "calendar/fetchAppointmentDetail",
  async (appointmentId, { getState, rejectWithValue }) => {
    try {
      const token = getState().login?.token;
      if (!token) {
        throw new Error("Authentication required");
      }

      const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

      const response = await fetch(
        `${baseUrl}/calendar/appointments/${appointmentId}/`,
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createAppointment = createAsyncThunk(
  "calendar/createAppointment",
  async (appointmentData, { getState, rejectWithValue }) => {
    try {
      const token = getState().login?.token;
      if (!token) {
        throw new Error("Authentication required");
      }
      const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

      const response = await fetch(`${baseUrl}/calendar/appointments/`, {
        method: "POST",
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(appointmentData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateAppointment = createAsyncThunk(
  "calendar/updateAppointment",
  async ({ id, ...appointmentData }, { getState, rejectWithValue }) => {
    try {
      const token = getState().login?.token;
      if (!token) {
        throw new Error("Authentication required");
      }
      const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

      const response = await fetch(`${baseUrl}/calendar/appointments/${id}/`, {
        method: "PUT",
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(appointmentData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteAppointment = createAsyncThunk(
  "calendar/deleteAppointment",
  async (id, { getState, rejectWithValue }) => {
    try {
      const token = getState().login?.token;
      if (!token) {
        throw new Error("Authentication required");
      }
      const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

      const response = await fetch(`${baseUrl}/calendar/appointments/${id}/`, {
        method: "DELETE",
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Job Setup Async Thunks
export const updateJobSetup = createAsyncThunk(
  "calendar/updateJobSetup",
  async ({ jobId, jobSetupData }, { getState, rejectWithValue }) => {
    try {
      const token = getState().login?.token;
      if (!token) {
        throw new Error("Authentication required");
      }

      const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

      const response = await fetch(`${baseUrl}/calendar/jobs/${jobId}/`, {
        method: "PUT",
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(jobSetupData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const affectedAppointments = await response.json();
      return affectedAppointments;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Alias for create - both use the same endpoint
export const createJobSetup = updateJobSetup;

// Calendar slice
const calendarSlice = createSlice({
  name: "calendar",
  initialState,
  reducers: {
    setView: (state, action) => {
      state.view = action.payload;
    },
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
    },
    setDateRange: (state, action) => {
      state.dateRange = action.payload;
    },
    setSelectedEvent: (state, action) => {
      state.selectedEvent = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.filters.searchQuery = action.payload;
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearErrors: (state) => {
      state.error = initialState.error;
    },
    clearJobSetupError: (state) => {
      state.error.jobSetup = null;
    },
    clearAppointmentDetailError: (state) => {
      state.error.appointmentDetail = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch appointments
    builder
      .addCase(fetchAppointmentsByDateRange.pending, (state) => {
        state.loading.appointments = true;
        state.error.appointments = null;
      })
      .addCase(fetchAppointmentsByDateRange.fulfilled, (state, action) => {
        state.loading.appointments = false;
        state.appointments = action.payload.appointments || [];
        state.metadata.total_count = action.payload.metadata?.total_count || 0;

        // Update date range
        if (action.payload.date_range) {
          state.dateRange = action.payload.date_range;
        }
      })
      .addCase(fetchAppointmentsByDateRange.rejected, (state, action) => {
        state.loading.appointments = false;
        state.error.appointments = action.payload;
      });

    // Fetch settings
    builder
      .addCase(fetchCalendarSettings.pending, (state) => {
        state.loading.settings = true;
        state.error.settings = null;
      })
      .addCase(fetchCalendarSettings.fulfilled, (state, action) => {
        state.loading.settings = false;
        state.settings = action.payload;
      })
      .addCase(fetchCalendarSettings.rejected, (state, action) => {
        state.loading.settings = false;
        state.error.settings = action.payload;
      });

    // Fetch section templates
    builder
      .addCase(fetchSectionTemplates.pending, (state) => {
        state.loading.sectionTemplates = true;
        state.error.sectionTemplates = null;
      })
      .addCase(fetchSectionTemplates.fulfilled, (state, action) => {
        state.loading.sectionTemplates = false;
        state.sectionTemplates = action.payload;
      })
      .addCase(fetchSectionTemplates.rejected, (state, action) => {
        state.loading.sectionTemplates = false;
        state.error.sectionTemplates = action.payload;
      });

    // Fetch appointment detail
    builder
      .addCase(fetchAppointmentDetail.pending, (state) => {
        state.loading.appointmentDetail = true;
        state.error.appointmentDetail = null;
      })
      .addCase(fetchAppointmentDetail.fulfilled, (state, action) => {
        state.loading.appointmentDetail = false;
        // Update the specific appointment in the appointments array with detailed data
        const detailedAppointment = action.payload;
        const index = state.appointments.findIndex(
          (apt) => apt.id === detailedAppointment.id
        );
        if (index !== -1) {
          state.appointments[index] = detailedAppointment;
        }
        // Also update selectedEvent if it matches
        if (state.selectedEvent?.id === detailedAppointment.id) {
          state.selectedEvent = detailedAppointment;
        }
      })
      .addCase(fetchAppointmentDetail.rejected, (state, action) => {
        state.loading.appointmentDetail = false;
        state.error.appointmentDetail = action.payload;
      });

    // Create appointment
    builder
      .addCase(createAppointment.pending, (state) => {
        state.loading.create = true;
        state.error.create = null;
      })
      .addCase(createAppointment.fulfilled, (state, action) => {
        state.loading.create = false;
        // Add new appointment to state
        state.appointments.push(action.payload);
      })
      .addCase(createAppointment.rejected, (state, action) => {
        state.loading.create = false;
        state.error.create = action.payload;
      });

    // Update appointment
    builder
      .addCase(updateAppointment.pending, (state) => {
        state.loading.update = true;
        state.error.update = null;
      })
      .addCase(updateAppointment.fulfilled, (state, action) => {
        state.loading.update = false;
        // Update appointment in state
        const index = state.appointments.findIndex(
          (apt) => apt.id === action.payload.id
        );
        if (index !== -1) {
          state.appointments[index] = action.payload;
        }
      })
      .addCase(updateAppointment.rejected, (state, action) => {
        state.loading.update = false;
        state.error.update = action.payload;
      });

    // Delete appointment
    builder
      .addCase(deleteAppointment.pending, (state) => {
        state.loading.delete = true;
        state.error.delete = null;
      })
      .addCase(deleteAppointment.fulfilled, (state, action) => {
        state.loading.delete = false;
        // Remove appointment from state
        state.appointments = state.appointments.filter(
          (apt) => apt.id !== action.payload
        );
      })
      .addCase(deleteAppointment.rejected, (state, action) => {
        state.loading.delete = false;
        state.error.delete = action.payload;
      });

    // Job Setup operations
    builder
      .addCase(updateJobSetup.pending, (state) => {
        state.loading.jobSetup = true;
        state.error.jobSetup = null;
      })
      .addCase(updateJobSetup.fulfilled, (state, action) => {
        state.loading.jobSetup = false;
        // Update affected appointments based on their IDs
        const affectedAppointments = action.payload;
        if (Array.isArray(affectedAppointments)) {
          affectedAppointments.forEach((updatedAppointment) => {
            const appointmentIndex = state.appointments.findIndex(
              (apt) => apt.id === updatedAppointment.id
            );
            if (appointmentIndex !== -1) {
              state.appointments[appointmentIndex] = updatedAppointment;
            }
          });
        }
      })
      .addCase(updateJobSetup.rejected, (state, action) => {
        state.loading.jobSetup = false;
        state.error.jobSetup = action.payload;
      });
  },
});

// Export actions
export const {
  setView,
  setSelectedDate,
  setDateRange,
  setSelectedEvent,
  setSearchQuery,
  clearFilters,
  clearErrors,
  clearJobSetupError,
  clearAppointmentDetailError,
} = calendarSlice.actions;

// Selectors
export const selectCalendarEvents = (state) => {
  return state.calendar.appointments;
};

export const selectFilteredEvents = (state) => {
  const events = selectCalendarEvents(state);
  const { filters } = state.calendar;
  const { searchQuery } = filters;

  if (!searchQuery) return events;

  const query = searchQuery.toLowerCase();
  return events.filter((event) => {
    const addressStr = event.job?.report?.address
      ? typeof event.job.report.address === "string"
        ? event.job.report.address
        : [
            event.job.report.address.address_line1,
            event.job.report.address.city,
            event.job.report.address.state,
          ]
            .filter(Boolean)
            .join(" ")
      : "";
    return (
      event.title?.toLowerCase().includes(query) ||
      addressStr.toLowerCase().includes(query) ||
      event.job?.note?.toLowerCase().includes(query) ||
      event.job?.report?.name?.toLowerCase().includes(query)
    );
  });
};
export const selectEventsByDate = (date) => (state) => {
  const targetDate = moment(date).format("YYYY-MM-DD");
  const events = selectCalendarEvents(state);
  return events.filter((event) => {
    const eventDate = moment(event.start).format("YYYY-MM-DD");
    return eventDate === targetDate;
  });
};
export const selectCurrentDateRange = (state) => state.calendar.dateRange;
export const selectUserTimezone = (state) =>
  state.calendar.settings?.timezone ||
  Intl.DateTimeFormat().resolvedOptions().timeZone;
export const selectCalendarView = (state) => state.calendar.view;
export const selectCalendarLoading = (state) => state.calendar.loading;
export const selectCalendarErrors = (state) => state.calendar.error;
export const selectSectionTemplates = (state) =>
  state.calendar.sectionTemplates;

export default calendarSlice.reducer;
